import React, { useState } from 'react';
import { Calendar, Clock, User, Phone, Mail, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent } from './ui/card';
import { Calendar as CalendarComponent } from './ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { format } from 'date-fns';
import { toast } from 'sonner';

const Appointment = () => {
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    time: '',
    newPatient: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Please select a preferred date');
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Appointment request submitted! We\'ll confirm your booking soon.');
  };

  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
    '4:00 PM', '4:30 PM', '5:00 PM',
  ];

  const benefits = [
    'Free comprehensive consultation',
    'Digital X-rays included',
    'Personalized treatment plan',
    'Transparent pricing discussion',
    'No obligation to proceed',
  ];

  if (isSubmitted) {
    return (
      <section id="appointment" className="py-24 bg-teal-600">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-2xl animate-fade-in-up">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-teal-600" />
            </div>
            <h3 className="text-3xl font-bold text-slate-800 mb-4">
              Appointment Request Received!
            </h3>
            <p className="text-lg text-slate-600 mb-8">
              Thank you for booking with us. Our team will contact you within 
              24 hours to confirm your appointment.
            </p>
            <div className="bg-slate-50 rounded-2xl p-6 text-left mb-8">
              <h4 className="font-semibold text-slate-800 mb-4">Booking Details:</h4>
              <div className="space-y-2 text-slate-600">
                <p><strong>Name:</strong> {formData.firstName} {formData.lastName}</p>
                <p><strong>Date:</strong> {date ? format(date, 'PPP') : 'Not selected'}</p>
                <p><strong>Preferred Time:</strong> {formData.time || 'Any available'}</p>
                <p><strong>Service:</strong> {formData.service || 'Consultation'}</p>
              </div>
            </div>
            <Button
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  service: '',
                  time: '',
                  newPatient: '',
                  notes: '',
                });
                setDate(null);
              }}
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-3"
            >
              Book Another Appointment
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="appointment" className="py-24 bg-teal-600 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 right-20 w-96 h-96 bg-teal-500/50 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-72 h-72 bg-teal-400/30 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-white/20 text-white font-semibold text-sm rounded-full mb-4 backdrop-blur-sm">
            Book Online
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Schedule Your Free Consultation
          </h2>
          <p className="text-lg text-teal-100 leading-relaxed">
            Take the first step towards your dream smile. Book your complimentary 
            consultation today and let's create your personalized treatment plan.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Benefits Sidebar */}
          <div className="lg:col-span-2 animate-slide-in-left">
            <Card className="border-0 bg-white/10 backdrop-blur-md">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-white mb-6">
                  Your Free Consultation Includes:
                </h3>
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li
                      key={benefit}
                      className="flex items-start gap-3 text-teal-50"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <CheckCircle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-8 border-t border-white/20">
                  <h4 className="font-semibold text-white mb-4">Prefer to call?</h4>
                  <a
                    href="tel:5551234567"
                    className="flex items-center gap-3 text-white hover:text-amber-300 transition-colors"
                  >
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm text-teal-200">Call us at</p>
                      <p className="font-semibold text-lg">(555) 123-4567</p>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Appointment Form */}
          <Card className="lg:col-span-3 border-0 shadow-2xl animate-slide-in-right">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Fields */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-slate-700">
                      First Name *
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John"
                        required
                        className="h-12 pl-11 border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-slate-700">
                      Last Name *
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Smith"
                      required
                      className="h-12 border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>
                </div>

                {/* Contact Fields */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">
                      Email Address *
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                        className="h-12 pl-11 border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700">
                      Phone Number *
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        required
                        className="h-12 pl-11 border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Service and Patient Type */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700">Service Interested In</Label>
                    <Select
                      value={formData.service}
                      onValueChange={(value) => handleSelectChange('service', value)}
                    >
                      <SelectTrigger className="h-12 border-slate-200 focus:border-teal-500 focus:ring-teal-500">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="consultation">Free Consultation</SelectItem>
                        <SelectItem value="braces">Traditional Braces</SelectItem>
                        <SelectItem value="invisalign">Invisalign</SelectItem>
                        <SelectItem value="ceramic">Ceramic Braces</SelectItem>
                        <SelectItem value="retainers">Retainers</SelectItem>
                        <SelectItem value="early">Early Treatment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Are you a new patient? *</Label>
                    <Select
                      value={formData.newPatient}
                      onValueChange={(value) => handleSelectChange('newPatient', value)}
                      required
                    >
                      <SelectTrigger className="h-12 border-slate-200 focus:border-teal-500 focus:ring-teal-500">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes, I'm new</SelectItem>
                        <SelectItem value="no">No, I'm an existing patient</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Date and Time */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700">Preferred Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full h-12 justify-start text-left font-normal border-slate-200 hover:bg-slate-50"
                        >
                          <Calendar className="mr-2 h-5 w-5 text-slate-400" />
                          {date ? (
                            format(date, 'PPP')
                          ) : (
                            <span className="text-slate-400">Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) =>
                            date < new Date() || date.getDay() === 0
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Preferred Time</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
                      <Select
                        value={formData.time}
                        onValueChange={(value) => handleSelectChange('time', value)}
                      >
                        <SelectTrigger className="h-12 pl-11 border-slate-200 focus:border-teal-500 focus:ring-teal-500">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((slot) => (
                            <SelectItem key={slot} value={slot}>
                              {slot}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-slate-700">
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Any special requests or information we should know..."
                    rows={3}
                    className="border-slate-200 focus:border-teal-500 focus:ring-teal-500 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg btn-transition shadow-lg shadow-teal-600/20"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Request Appointment
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>

                <p className="text-center text-sm text-slate-500">
                  By submitting, you agree to our privacy policy. We'll contact you 
                  within 24 hours to confirm your appointment.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Appointment;
